import angular from "angular";
import { ApiValidationErrorType, Arrays, Dates, DateTimes, Message, MessageThread, Numeric, ThreadedMessageType, MessageFrom, TimesheetInput, TimesheetInputModification } from "@salaxy/core";

import { SessionService, UiHelpers } from "../../services";
import { ListControllerBase } from "../bases";


/** Controller handles Timer usecases */
export class WorkerTimerController extends ListControllerBase<MessageThread, Message> {
  /** Binded crud controller */
  public crudController: any;

  /** User interface mode: "mini" is the front page card UI without the list and "edit" is just the editable list of entries.  */
  public timerMode: "mini" | "edit" | "";

  private timer: angular.IPromise<void>;

  private _newTimerMessage: Message;

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["$interval", "UiHelpers", "SessionService"];

  /** Constructor for dependency injection */
  constructor(
    private $interval: angular.IIntervalService,
    uiHelpers: UiHelpers,
    private sessionService: SessionService,
  ) {
    super(uiHelpers);
  }

  /**
   * Controller initializations
   */
  public $onInit(){
    if (this.isTimerOn) {
      this.startTechnicalTimer(this.getTimerMessage());
    }
    this.validateEntries();
    this.onCommit = () => {
      this.validateEntries();
      if (this.isTimerOn) {
        this.startTechnicalTimer(this.getTimerMessage());
      }
    };
    super.$onInit();
  }

  /**
   * Stop timer on destroy
   */
  public $onDestoy() {
    this.stopTechnicalTimer();
  }

  /**
   * Returns true if the message indicates that the timer should be on.
   */
  public get isTimerOn() {
    const timerMessage = this.getTimerMessage();
    return timerMessage.contentData.startTime && !timerMessage.contentData.endTime;
  }

  /**
   * Returns true if the message indicates that the timer should be on.
   */
    public get isTimerOnFromDayBefore() {
      const timerMessage = this.getTimerMessage();
      return this.isTimerOn && Dates.isBefore(timerMessage.contentData.period.start, Dates.getToday());
    }

  /**
   * Ensure that atleast one message exists
   */
  public getTimerMessage() {
    const messageIndex = this.list.findIndex(x => x.type === ThreadedMessageType.Timer &&
      (
        (!x.contentData.startTime && !x.contentData.endTime) ||
        (x.contentData.startTime && !x.contentData.endTime)
      )
    );

    if (messageIndex >= 0) {
      this._newTimerMessage = null;
      return this.list[messageIndex];
    }

    if (!this._newTimerMessage) {
      this._newTimerMessage = this.getBlank();
    }
    return this._newTimerMessage;
  }


  /**
   * List of items
   */
  public get list(): Message[] {
    if (!this.parent) {
      return null;
    }
    return this.parent.messages;
  }


  /** Template for edit UI that is shown in a modal dialog. */
  public getEditDialogTemplateUrl(): string {
    return "salaxy-components/worker/TimerEditDialog.html";
  }

  /**
   * Creating of a new item
   */
  public getBlank(): Message {

    const contentData: TimesheetInput = {
      modification: TimesheetInputModification.CREATE,
      period: {
        start: Dates.getToday()
      }
    };

    return {
      type: ThreadedMessageType.Timer,
      content: "Kellokorttikirjaus",
      contentData,
      fromParty: MessageFrom.OtherParty,
    };
  }

  /** Starts the timer. */
  public startTimer() {
    this.stopTechnicalTimer();
    let timerMessage = this.getTimerMessage();
    if (this.list.indexOf(timerMessage) < 0) {
      this.list.push(timerMessage);
    }
    const now = new Date();
    timerMessage.contentData.period.start = Dates.asDate(now);
    timerMessage.contentData.startTime = DateTimes.asTime(now);
    timerMessage.contentData.totalTime = 0;
    timerMessage.contentData.totalTimeInSeconds = 0;
    this.crudController.save().then((thread: MessageThread) => {
      this.parent = thread;
      timerMessage = this.getTimerMessage();
      this.startTechnicalTimer(timerMessage);
    });
  }

  /** Stops the timer. */
  public stopTimer() {
    this.stopTechnicalTimer();
    const timerMessage = this.getTimerMessage();
    const now = new Date();
    timerMessage.contentData.period.end = Dates.asDate(now);
    timerMessage.contentData.endTime = DateTimes.asTime(now);
    this.calculateAndSetTotals(timerMessage);
    this.crudController.save().then((thread) => {
      this.parent = thread;
      this.validateEntries();
    });
  }

  /**
   * Formats time as minutes or seconds.
   * TODO: implement this as filter.
   * @param total Total number of minutes or seconds
   * @param unit Unit: seconds or minutes
   * @returns Formatted time
   */
  public formatTime(total: number, unit: "minutes" | "seconds" = "minutes"): string {
    const padTo2Digits = (num) => {
      return num.toString().padStart(2, "0");
    };
    total = Numeric.isNumber(total) ? total : 0;
    switch( unit ) {
      case "minutes": {
        const hours = Math.floor(total / 60);
        const minutes = total % 60;
        return `${padTo2Digits(hours)}:${padTo2Digits(minutes)}`;
      }
      case "seconds": {
        const hours = Math.floor(total / 3600);
        const minutes =  Math.floor((total % 3600) / 60);
        const seconds = total % 60;
        return `${padTo2Digits(hours)}:${padTo2Digits(minutes)}:${padTo2Digits(seconds)}`;
      }
    }
  }

  /**
   * Gets the total of the timesheets logged in messageThread.
   */
  public getAndFormatTotal(): string {
    return this.formatTime(Arrays.sum(this.list, (x) => x.contentData.totalTime));
  }

  /**
   * Override for ListControllerBase showEditDialog
   * @param item Item to edit or string "new" for creating a new one.
   * @param isNew Optional way of specifying that the item is a new item (with default values, not yet added to the list).
   * If item is string "new", this parameter will have no effect (will always be true).
   */
  public showEditDialog(item: Message | "new", isNew = false) {
    if (!item) {
      throw new Error("showEditDialog called without a parameter.");
    }
    if (item === "new") {
      isNew = true;
      item = this.getBlank();
    }

    const lk1 = ( this.crudController?.employingRelation?.settings?.costAccounting?.dimensions ||
      this.sessionService.session.settings?.costAccounting?.dimensions || undefined)
      .find((x) => x.name = "lk1");


    const costAccountingSettings = lk1 ?
      {
        options: lk1.options,
        label: lk1.label,
      } : null;

    this.uiHelpers.openEditDialog(this.getEditDialogTemplateUrl(), item, { ...this.getEditDialogLogic(), costAccountingSettings }).then((result) => {
      if (result.action === "ok" || result.action === "ok-no-save") {
        this.calculateAndSetTotals(result.item);

        if (isNew) {
          this.list.push(result.item);
        }
        if ((isNew || result.hasChanges) && result.action !== "ok-no-save") {
          this.commit();
        }
      } else if (result.action === "delete" || result.action === "delete-no-save") {
        if (!isNew) {
          this.delete(item as Message);
          if (result.action !== "delete-no-save") {
            this.commit();
          }
        }
      } else {
        // No changes, cancel
      }
    });
  }

  /** Gets the project text for a project */
  public getDimensionOptionText(value: string): string {
    if (value) {
      const lk1 = ( this.crudController?.employingRelation?.settings?.costAccounting?.dimensions ||
        this.sessionService.session.settings?.costAccounting?.dimensions || undefined )
      .find((x) => x.name = "lk1");
      if (lk1) {
          return lk1.options.find( (x) => x.value === value)?.text ?? "";
      }
    }
    return "";
  }


  private startTechnicalTimer(timerMessage: Message) {
    this.stopTechnicalTimer();
    this.timer = this.$interval((msg) => {
      this.calculateAndSetTotals(msg, new Date());
    }, 1000, 0, true, timerMessage);
  }

  private calculateAndSetTotals(timerMessage: Message, endTime: Date = null) {
    const now = new Date();
    const startTime = new Date(`${timerMessage.contentData.period.start ?? Dates.asDate(now) }T${timerMessage.contentData.startTime ?? DateTimes.asTime(now)}:00`);
    endTime = endTime ?? new Date(`${timerMessage.contentData.period.end ?? Dates.asDate(now)}T${timerMessage.contentData.endTime ?? DateTimes.asTime(now)}:00`);
    const ms = endTime.getTime() - startTime.getTime();
    timerMessage.contentData.totalTimeInSeconds = Math.floor(ms / 1000);
    timerMessage.contentData.totalTime = Math.floor(ms / 60000);
  }
  private stopTechnicalTimer(): void {
    if (this.timer) {
      this.$interval.cancel(this.timer);
    }
  }

  /** Overlapping timer log entries validation */
  private validateEntries(): void {
    for (const entry of this.list) {
      entry.validation = null;
    }
    for (const entry of this.list) {
      if (entry.contentData.period.start &&
        entry.contentData.startTime &&
        entry.contentData.period.end &&
        entry.contentData.endTime) {

        const entryStartTime = new Date(`${entry.contentData.period.start}T${entry.contentData.startTime}:00`);
        const entryEndTime = new Date(`${entry.contentData.period.end}T${entry.contentData.endTime}:00`);

        const isOverlapping = this.list.find((x) => {

          if (x.contentData.period.start &&
            x.contentData.startTime &&
            x.contentData.period.end &&
            x.contentData.endTime) {

            const xStartTime = new Date(`${x.contentData.period.start}T${x.contentData.startTime}:00`);
            const xEndTime = new Date(`${x.contentData.period.end}T${x.contentData.endTime}:00`);

            return entry != x &&
              (
                // endTime not inside entry's range
                (Dates.isSameOrAfter(xEndTime, entryStartTime, "minutes") && Dates.isSameOrBefore(xEndTime, entryEndTime, "minutes")) ||
                // startTime not inside entry's range
                (Dates.isSameOrAfter(xStartTime, entryStartTime, "minutes") && Dates.isBefore(xStartTime, entryEndTime, "minutes")) ||
                // another range covers entry's range
                (Dates.isSameOrBefore(xStartTime, entryStartTime, "minutes") && Dates.isSameOrAfter(xEndTime, entryEndTime, "minutes"))
              );
          }
          else {
            return false;
          }
        });
        if (isOverlapping) {
          entry.validation = {
            errors: [{
              type: "invalid" as ApiValidationErrorType,
              msg: "Päällekkäisiä kirjauksia",
              code: "TimeSheetInput.isOverlapping"
            }],
            isValid: false,
            hasAllRequiredFields: true
          };
        }
      }
    }
  }
}
